import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { donationAmounts, paymentMethods, campaignSelectOptions, faqItems, impactStats } from '@/mocks/donateData';

export default function DonatePage() {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [donationType, setDonationType] = useState<'one-time' | 'monthly'>('one-time');
  const [selectedCampaign, setSelectedCampaign] = useState('general');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorPhone, setDonorPhone] = useState('');
  const [donorPan, setDonorPan] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const effectiveAmount = selectedAmount || (customAmount ? Number(customAmount) : null);

  const handleDonate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveAmount || effectiveAmount < 10) return;
    setShowSuccess(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setSelectedAmount(null);
    setCustomAmount('');
    setDonorName('');
    setDonorEmail('');
    setDonorPhone('');
    setDonorPan('');
    setShowSuccess(false);
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-20 pb-14 md:pt-28 md:pb-20 bg-gradient-to-br from-primary-500 via-primary-400 to-accent-400 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/4 blur-3xl"></div>
          <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white/90 text-xs font-medium rounded-full mb-4">
              <i className="ri-heart-line mr-1.5"></i>Make a Difference
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">Support a Cause, Change a Life</h1>
            <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto">
              Your generosity fuels our mission. 100% secure payments, tax benefits available, and complete transparency on fund utilization.
            </p>
          </div>
        </section>

        {/* Success Modal Overlay */}
        {showSuccess && (
          <section className="py-8 bg-green-50 border-b border-green-200">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
              <div className="bg-white rounded-2xl p-6 md:p-10 border border-green-200 max-w-2xl mx-auto text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <i className="ri-check-line text-green-600 text-3xl"></i>
                </div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2">Thank You for Your Generosity!</h2>
                <p className="text-foreground-500 text-sm mb-6">
                  Your {donationType === 'monthly' ? 'monthly' : ''} donation of <strong>₹{effectiveAmount?.toLocaleString()}</strong> to <strong>{campaignSelectOptions.find((c) => c.value === selectedCampaign)?.label}</strong> has been initiated. A tax receipt will be sent to your email shortly.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={resetForm}
                    className="px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Make Another Donation
                  </button>
                  <Link
                    to="/campaigns"
                    className="px-6 py-2.5 bg-background-100 text-foreground-700 text-sm font-semibold rounded-full hover:bg-background-200 transition-colors whitespace-nowrap"
                  >
                    Explore More Campaigns
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="py-12 md:py-18 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">

              {/* Main Donation Form - 2/3 */}
              <div className="lg:col-span-2 space-y-8">
                {/* Donation Type Toggle */}
                <div className="bg-white rounded-2xl p-6 border border-background-200">
                  <div className="flex bg-background-100 rounded-full p-1 mb-0">
                    <button
                      onClick={() => setDonationType('one-time')}
                      className={`flex-1 py-2.5 text-sm font-semibold rounded-full transition-all duration-200 ${
                        donationType === 'one-time'
                          ? 'bg-white text-foreground-950 shadow-sm'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      One Time
                    </button>
                    <button
                      onClick={() => setDonationType('monthly')}
                      className={`flex-1 py-2.5 text-sm font-semibold rounded-full transition-all duration-200 ${
                        donationType === 'monthly'
                          ? 'bg-white text-foreground-950 shadow-sm'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      <span className="flex items-center justify-center gap-1.5">
                        Monthly
                        <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-[10px] rounded-full font-bold">Recommended</span>
                      </span>
                    </button>
                  </div>
                  {donationType === 'monthly' && (
                    <p className="text-foreground-500 text-xs mt-3 text-center">
                      Your contribution will be auto-debited each month. Cancel anytime from your donor dashboard.
                    </p>
                  )}
                </div>

                {/* Amount Selection */}
                <div className="bg-white rounded-2xl p-6 border border-background-200">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-5">Select Donation Amount</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                    {donationAmounts.map((amount) => (
                      <button
                        key={amount.value}
                        onClick={() => { setSelectedAmount(amount.value); setCustomAmount(''); }}
                        className={`p-4 rounded-xl border-2 text-left transition-all duration-200 group ${
                          selectedAmount === amount.value
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-background-200 hover:border-primary-300 bg-background-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            selectedAmount === amount.value ? 'bg-primary-500' : 'bg-background-200'
                          }`}>
                            <i className={`${amount.icon} text-sm ${
                              selectedAmount === amount.value ? 'text-white' : 'text-foreground-500'
                            }`}></i>
                          </div>
                          <span className={`font-bold text-base ${
                            selectedAmount === amount.value ? 'text-primary-600' : 'text-foreground-900'
                          }`}>{amount.label}</span>
                        </div>
                        <p className="text-foreground-400 text-[11px] leading-tight">{amount.desc}</p>
                      </button>
                    ))}
                  </div>

                  {/* Custom Amount */}
                  <div className="relative">
                    <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Or enter a custom amount</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 font-semibold text-sm">₹</span>
                      <input
                        type="number"
                        value={customAmount}
                        onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                        placeholder="Enter amount (min ₹10)"
                        min="10"
                        className="w-full pl-9 pr-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Campaign Selection */}
                <div className="bg-white rounded-2xl p-6 border border-background-200">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">Choose a Cause</h3>
                  <select
                    value={selectedCampaign}
                    onChange={(e) => setSelectedCampaign(e.target.value)}
                    className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 appearance-none cursor-pointer"
                    style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  >
                    {campaignSelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Donor Information */}
                <div className="bg-white rounded-2xl p-6 border border-background-200">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-5">Your Information</h3>
                  <form onSubmit={handleDonate} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Full Name *</label>
                        <input
                          type="text"
                          value={donorName}
                          onChange={(e) => setDonorName(e.target.value)}
                          required
                          placeholder="Enter your full name"
                          className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Email Address *</label>
                        <input
                          type="email"
                          value={donorEmail}
                          onChange={(e) => setDonorEmail(e.target.value)}
                          required
                          placeholder="your@email.com"
                          className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Phone Number</label>
                        <input
                          type="tel"
                          value={donorPhone}
                          onChange={(e) => setDonorPhone(e.target.value)}
                          placeholder="+91 98765 43210"
                          className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">PAN Number (for tax receipt)</label>
                        <input
                          type="text"
                          value={donorPan}
                          onChange={(e) => setDonorPan(e.target.value.toUpperCase())}
                          placeholder="ABCDE1234F"
                          maxLength={10}
                          className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 uppercase placeholder:text-foreground-400"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={!effectiveAmount || effectiveAmount < 10}
                      className="w-full py-4 bg-primary-500 text-white text-base font-bold rounded-xl hover:bg-primary-600 disabled:bg-foreground-300 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 mt-2"
                    >
                      <i className="ri-secure-payment-line text-lg"></i>
                      {effectiveAmount && effectiveAmount >= 10
                        ? `Proceed to Pay ₹${effectiveAmount.toLocaleString()} ${donationType === 'monthly' ? '/month' : ''}`
                        : 'Select an amount to continue'}
                    </button>
                  </form>
                </div>
              </div>

              {/* Sidebar - 1/3 */}
              <div className="space-y-6">
                {/* Payment Methods */}
                <div className="bg-white rounded-2xl p-6 border border-background-200">
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">Payment Methods</h3>
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <div key={method.name} className="flex items-center gap-3 p-3 rounded-xl bg-background-50">
                        <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center flex-shrink-0">
                          <i className={`${method.icon} text-foreground-600 text-lg`}></i>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-foreground-900">{method.name}</div>
                          <div className="text-[11px] text-foreground-400">{method.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-background-100">
                    <i className="ri-shield-check-line text-green-600 text-sm"></i>
                    <span className="text-xs text-foreground-500">256-bit SSL encrypted · PCI-DSS compliant</span>
                  </div>
                </div>

                {/* Impact Stats */}
                <div className="bg-white rounded-2xl p-6 border border-background-200">
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">Your Impact</h3>
                  <div className="space-y-3">
                    {impactStats.map((stat) => (
                      <div key={stat.label} className="flex items-center justify-between py-2.5 border-b border-background-100 last:border-0">
                        <span className="text-sm text-foreground-600">{stat.label}</span>
                        <span className="text-base font-bold text-foreground-950">{stat.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Trust Badge */}
                <div className="bg-gradient-to-br from-accent-100 to-primary-100 rounded-2xl p-6 border border-accent-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
                      <i className="ri-verified-badge-line text-primary-600 text-2xl"></i>
                    </div>
                    <div>
                      <div className="font-bold text-foreground-950 text-sm">80G Tax Benefit</div>
                      <div className="text-foreground-500 text-xs">Save up to 50% on taxes</div>
                    </div>
                  </div>
                  <p className="text-foreground-600 text-xs leading-relaxed">
                    All donations to Happy Faces Society are eligible for tax deduction under Section 80G of the Income Tax Act. You will receive an automated tax receipt via email.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">FAQ</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Frequently Asked Questions</h2>
              <p className="text-foreground-500 text-base">Everything you need to know about donating to Happy Faces Society.</p>
            </div>
            <div className="space-y-3">
              {faqItems.map((faq, i) => (
                <div key={i} className="bg-white rounded-2xl border border-background-200 overflow-hidden">
                  <button
                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.question}</span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      activeFaq === i ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-500'
                    }`}>
                      <i className={`text-sm transition-transform duration-300 ${activeFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      activeFaq === i ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-5 pb-5 text-foreground-500 text-sm leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}